import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import { useForm, Controller } from 'react-hook-form';

import { Button } from 'components/core/Button';
import { Form, FormItem, FormGroup, TextInput } from 'components/core/Form';
import { H3, Paragraph } from 'components/core/Typography';
import { IconText } from 'components/core/IconText';
import { Link } from 'components/core/Link';

import { required, email as emailValidator, phone as phoneValidator } from 'utils/form/validation';

import './hoursAndDirectionsForm.scss';

function validateRequired(value) {
	return required(value) || true;
}

function validateEmail(value) {
	return required(value) || emailValidator(value) || true;
}

function validatePhone(value) {
	return required(value) || phoneValidator(value) || true;
}

export function HoursAndDirectionsForm({
	onClose, onSubmit: onSubmitProp, facilityId
}) {
	const [directionsSent, setDirectionsSent] = useState({});
	const [submitSucceeded, setSubmitSucceeded] = useState(false);
	const [formError, setFormError] = useState(null);

	const { control, handleSubmit, formState: { isSubmitting } } = useForm({
		mode: 'onBlur',
		defaultValues: {
			firstName: '',
			lastName: '',
			email: '',
			phone: '',
		},
	});

	const wrappedSubmit = (data) => onSubmitProp({
		...data,
		facilityId,
		uri: typeof window !== 'undefined' && window.location.href
	}).then(({ error: submitError, payload }) => {
		if (submitError) {
			setFormError(submitError.message);
			return;
		}

		// Removes starting address from map link set in Phacade/Genesis
		// saddr was repeat of faciltiy address
		payload.link.url = get(payload, 'link.url')?.replace(/(saddr=)[^&]*/, '$1');

		setDirectionsSent(payload);
		setSubmitSucceeded(true);
	});

	return (
		<div className="hours-and-directions-form">
			<H3 size="medium">
				Get facility hours and directions
			</H3>
			{!submitSucceeded &&
				<Form
					onSubmit={handleSubmit(wrappedSubmit)}
					successLabel="form submit success"
					failureLabel="form submit failure"
					id="hours-and-directions-form"
				>
					{!!formError &&
						<IconText type="alert">
							{formError}
						</IconText>
					}
					<FormGroup>
						<FormItem>
							<Controller
								name="firstName"
								control={control}
								rules={{ validate: validateRequired }}
								render={({ field, fieldState }) => (
									<TextInput
										input={field}
										meta={{
											touched: fieldState.isTouched || fieldState.invalid,
											error: fieldState.error?.message,
											valid: !fieldState.invalid,
										}}
										label="First name"
										errorMessage="Please provide your first name"
									/>
								)}
							/>
						</FormItem>
						<FormItem>
							<Controller
								name="lastName"
								control={control}
								rules={{ validate: validateRequired }}
								render={({ field, fieldState }) => (
									<TextInput
										input={field}
										meta={{
											touched: fieldState.isTouched || fieldState.invalid,
											error: fieldState.error?.message,
											valid: !fieldState.invalid,
										}}
										label="Last name"
										errorMessage="Please provide your last name"
									/>
								)}
							/>
						</FormItem>
					</FormGroup>
					<FormGroup>
						<FormItem>
							<Controller
								name="email"
								control={control}
								rules={{ validate: validateEmail }}
								render={({ field, fieldState }) => (
									<TextInput
										input={field}
										meta={{
											touched: fieldState.isTouched || fieldState.invalid,
											error: fieldState.error?.message,
											valid: !fieldState.invalid,
										}}
										label="Email address"
										type="email"
										errorMessage="Please provide a valid email address"
									/>
								)}
							/>
						</FormItem>
						<FormItem>
							<Controller
								name="phone"
								control={control}
								rules={{ validate: validatePhone }}
								render={({ field, fieldState }) => (
									<TextInput
										input={field}
										meta={{
											touched: fieldState.isTouched || fieldState.invalid,
											error: fieldState.error?.message,
											valid: !fieldState.invalid,
										}}
										label="Phone number"
										type="text"
										errorMessage="Please provide a valid phone number"
									/>
								)}
							/>
						</FormItem>
					</FormGroup>
					<FormGroup>
						<FormItem>
							<Button
								type="submit"
								disabled={isSubmitting}
							>
								Email directions
							</Button>
						</FormItem>
					</FormGroup>
					<Paragraph last className="tos">
						By submitting your email address, you are confirming that you have read, understood
						and accept our
						{' '}
						<Link href="/legal/consumer">
							Terms & Conditions
						</Link>.
					</Paragraph>
				</Form>
			}
			{submitSucceeded &&
				<div className="directions-sent">
					<Paragraph>
						{directionsSent.message}
					</Paragraph>
					<div className="directions-sent-actions">
						<Button
							dark
							onClick={onClose}
						>
							Got it, thanks!
						</Button>
						<Button
							href={get(directionsSent, 'link.url')}
							target="_blank"
							rel="noopener noreferrer"
						>
							{get(directionsSent, 'link.text')}
						</Button>
					</div>
				</div>
			}
		</div>
	);
}

HoursAndDirectionsForm.propTypes = {
	onClose: PropTypes.func,
	onSubmit: PropTypes.func,
	facilityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

export default HoursAndDirectionsForm;
